import { CurrencyPipe, DatePipe } from '@angular/common';
import { formatSystemDate } from 'src/app/shared/utils/system-date.util';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';

import { DialogTypeEnum, IDialogConfirm } from '../../../models/dialog';
import { IFilterBatchSearch, IFilterContracts, IFilterOptions } from '../../../models/filters';
import { HeaderTitle, IGenericResult, IHeaderItem, IHeaderSummary, ILot, IPagination, ISendBatchEmail } from '../../../models/generic';
import { IFieldOrder } from '../../../models/manual-batch';
import { ApiLotsService } from '../../../services/api/api-lots.service';
import { ApiDialogService } from '../../../services/api/api-cdialog.service';
import { DataService } from '../../../services/data.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { StatusBadgeTone } from '../status-badge/status-badge.component';
import { batchStatusTone as resolveBatchStatusTone } from 'src/app/shared/utils/status-badge.util';
import { ApiLotDetailService } from '../../../services/api/api-lot-detail.service';
import { DashFilterComponent } from '../dash-filter/dash-filter.component';

@Component({
  selector: 'app-listagem-lotes-faturamento',
  templateUrl: './listagem-lotes-faturamento.component.html',
  styleUrls: ['./listagem-lotes-faturamento.component.scss']
})
export class ListagemLotesFaturamentoComponent implements OnInit, OnDestroy {
  @Input() headerItems: IHeaderItem[] = [];

  title: HeaderTitle = 'Faturamento dos Contratos';
  lotsList: ILot[] = [];

  checkedBatches: string[] = [];
  exceptionsBatches: string[] = [];
  queryField = new FormControl<string>('', { nonNullable: true });
  batchDateFrom = new FormControl<string>('', { nonNullable: true });
  batchDateTo = new FormControl<string>('', { nonNullable: true });
  search!: string;
  filters!: IFilterContracts;
  showFilters: boolean = true;
  firsLotsList: boolean = false;
  checkAll: boolean = false;
  fieldsOrder: IFieldOrder[] = [];

  buildFiltersSubscription!: Subscription;

  count = 0;
  take = 50;
  skip = 0;
  firstLoad: boolean = true;
  isLoading: boolean = false;

  get canSendInvoicingEmail(): boolean {
    if (this.lotsList.length === 0)
      return false;

    if (this.checkAll)
      return this.selectedCount > 0 || this.hasMoreLotsToLoad;

    return this.checkedBatches.length > 0;
  }

  filter: IFilterOptions = {
    toggleFilter: () => (this.showFilters = !this.showFilters)
  };

  private static readonly ORDER_CYCLE: { [key: number]: number } = { 0: 1, 1: -1, [-1]: 0 };

  constructor(
    private _dataService: DataService,
    private _apiLotsService: ApiLotsService,
    private _dialogService: ApiDialogService,
    private _snackbarService: SnackbarService,
    private _datePipe: DatePipe,
    private _currencyPipe: CurrencyPipe,
    private _lotDetailService: ApiLotDetailService,
  ) { }

  getHeaderItem(label: string): IHeaderItem | undefined {
    return this.headerItems.find((h) => h.label === label);
  }

  sortIcon(label: string): string {
    const item = this.getHeaderItem(label);
    const value = item?.orderValue ?? 0;

    if (value === 1) return 'assets/icons/sort-up-icon.svg';
    if (value === -1) return 'assets/icons/sort-down-icon.svg';

    return 'assets/icons/sort-null-icon.svg';
  }

  toggleSort(item?: IHeaderItem): void {
    if (!item?.isOrder || !item.name) return;

    const nextOrder = ListagemLotesFaturamentoComponent.ORDER_CYCLE[item.orderValue ?? 0];

    // Ordenacao de uma coluna por vez (RN05): as demais voltam ao estado neutro.
    this.headerItems.forEach((header) => {
      if (header !== item) header.orderValue = 0;
    });
    item.orderValue = nextOrder;

    this.orderChange(nextOrder === 0 ? [] : [{ Field: item.name, Order: nextOrder }]);
  }

  batchStatusTone(lote: ILot): StatusBadgeTone {
    return resolveBatchStatusTone(lote.BatchStatus, { integrationError: !!lote.IntegrationError });
  }

  onlendingStatusTone(lote: ILot): StatusBadgeTone {
    return resolveBatchStatusTone(lote.StatusOfOnlending);
  }

  openLote(lote: ILot): void {
    const lotId = lote.BatchId.toString();

    if (lote.IntegrationError) {
      const dialogData: IDialogConfirm = {
        type: DialogTypeEnum.Optional,
        appearance: 'modern',
        template: {
          boldTitle: 'Erro de integração',
          title: `${lote.IntegrationMsg || '-'}`,
          confirm: 'Editar',
          dismiss: 'Voltar'
        }
      };

      this._dialogService.openDialog(dialogData).then((response) => {
        if (response) {
          this._lotDetailService.routeToDetailEvent.next({ lotId, type: 'Automatic' });
        }
      });
    } else {
      this._lotDetailService.routeToDetailEvent.next({ lotId, type: 'Automatic' });
    }
  }

  ngOnInit(): void {
    this.firsLotsList = true;
    this.getStorageFilter();
    this.buildFilters();
  }

  ngOnDestroy(): void {
    if (this.buildFiltersSubscription)
      this.buildFiltersSubscription.unsubscribe();
  }

  toggleFilter(): void {
    this.filter.toggleFilter();
  }

  private getBatches(pushLots: boolean = false, pagination?: IPagination): void {
    let searchData = {
      BatchStatuses: this.filters.BatchStatuses,
      BatchStatus: this.filters.BatchStatus,
      IsLegacy: this.filters.IsLegacy,
      Text: this.search,
      Skip: this.skip,
      Take: this.take,
      BatchDateFrom: this.batchDateFrom.value,
      BatchDateTo: this.batchDateTo.value,
      Banks: this.filters.Banks,
      OperationTypes: this.filters.OperationTypes,
      FieldsOrder: [...this.fieldsOrder]
    } as IFilterBatchSearch;

    if (pagination) {
      searchData = { ...searchData, ...pagination };
    }

    localStorage.setItem('bag_i', JSON.stringify({
      dataIni: this.batchDateFrom.value,
      dataFim: this.batchDateTo.value
    }));

    if (!pushLots)
      this.isLoading = true;

    this._apiLotsService.getLotsInvoicing(searchData).subscribe({
      next: (res) => {
        this.firsLotsList = false;
        this.count = res.Count;
        this.firstLoad = false;
        this.isLoading = false;

        if (pushLots)
          this.lotsList.push(...res.Data);
        else
          this.lotsList = res.Data;

        const summary: IHeaderSummary[] = res.Summary != null
          ? [
              { label: 'Lotes', value: res.Summary.TotalBatchs },
              { label: 'Contratos', value: res.Summary.TotalContracts },
              { label: 'Valor Faturado', value: this._currencyPipe.transform(res.Summary.BatchAmountSum, 'BRL') ?? '-' },
            ]
          : [];
        this._dataService.setHeaderSummary(summary);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  buildFilters(): void {
    this.buildFiltersSubscription = this._dataService.listFilters.subscribe(
      (filters) => {
        this.filters = filters;

        if (this.firstLoad) {
          const storageFilter = localStorage.getItem('bag_filter_i');

          if (storageFilter != null)
            this.filters = JSON.parse(storageFilter);
          else
            this.filters = this.getDefaultFilters();
        }
        this.clearSelection();
        this.resetPagination();
        this.getBatches();
      }
    );
  }

  clearAllFilters(dashFilter: DashFilterComponent): void {
    this.queryField.setValue('');
    this.search = '';
    this.batchDateFrom.setValue('');
    this.batchDateTo.setValue('');
    dashFilter.clearFilter();
  }

  searchField(text: string): void {
    let formattedString = text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '');

    this.search = formattedString;
    this.clearSelection();
    this.resetPagination();
    this.getBatches();
  }

  exportToClipboardInvoicing(): void {
    let searchData = {
      BatchStatuses: this.filters.BatchStatuses,
      BatchStatus: this.filters.BatchStatus,
      Text: this.search,
      Skip: 0,
      Take: 100000,
      BatchDateFrom: this.batchDateFrom.value,
      BatchDateTo: this.batchDateTo.value,
      IsLegacy: this.filters.IsLegacy,
      Banks: this.filters.Banks,
      OperationTypes: this.filters.OperationTypes,
      FieldsOrder: [...this.fieldsOrder]
    } as IFilterBatchSearch;

    if (this.firsLotsList) {
      searchData.BatchStatuses = [];
    }

    this._apiLotsService.getLotsInvoicing(searchData).subscribe({
      next: (res: IGenericResult<ILot[]>) => {
        let content: string = 'Status\tLote\tStatus de Recebimento\tData do Lote\tData de Recebimento\tData de envio e-mail\tBanco\tValor Faturado\tQt. de Contratos\tStatus Faturamento\r\n';

        if (res.Data.length > 0) {
          res.Data.forEach((lote: ILot) => {
            let status = lote.Active! ? 'Ativo' : 'Cancelado';
            let statusRecebimento = lote.PaymentDate ? 'Pago' : 'Pendente';
            let statusFaturamento = (lote.IntegrationError && 'Erro') || lote.BatchStatus || lote.StatusOfOnlending;

            content += `${status}\t ${lote.BatchNumber}\t${statusRecebimento}\t${formatSystemDate(lote.CreateDate, 'dd/MM/yyyy') || ''}\t${this._datePipe.transform(lote.PaymentDate, 'dd/MM/yyyy') || ''}\t${formatSystemDate(lote.EmailInvoiceDate, 'dd/MM/yyyy') || ''}\t${lote.Bank}\t${this._currencyPipe.transform(lote.AmountTotal, 'BRL')}\t${lote.NumberOfContracts}\t${statusFaturamento}\r\n`;
          });
        }

        navigator.clipboard.writeText(content).then(() => {
          this._snackbarService.openSnackBar('Copiado para área de transferência!', 'success');
        });
      }
    });
  }

  handlePagination(pagination: IPagination): void {
    this.take = pagination.Take;
    this.skip = pagination.Skip;
    this.getBatches(true);
  }

  private getDefaultFilters(): IFilterContracts {
    return {
      Status: [],
      Stages: [],
      StatusOfOnlending: [],
      OrderStatus: [],
      BatchStatuses: [],
      BatchStatus: true,
      IsLegacy: false,
      IsZeroRate: false,
      WithoutBatch: false,
      Banks: [],
      OperationTypes: [],
    };
  }

  changeInvoiceHeaderCheckbox(): void {
    this.checkAll = !this.checkAll;
    this.checkedBatches = [];
    this.exceptionsBatches = [];
  }

  checkCardFlag(batch: ILot): boolean {
    if (this.checkAll) {
      if (this.disabledCardFlag(batch)) {
        return false;
      }
      return !this.checkExceptionBatchId(batch.BatchId.toString());
    }
    return this.checkIncludedBatchId(batch.BatchId.toString());
  }

  disabledCardFlag(batch: ILot): boolean {
    return this.disabledReason(batch) !== null;
  }

  // Motivo do bloqueio do checkbox (null = lote selecionavel para envio de e-mail).
  disabledReason(batch: ILot): string | null {
    if (batch.BatchStatus?.toLowerCase() !== 'faturado')
      return 'Só lotes com status Faturado podem ser enviados por e-mail.';

    if (!batch.InvoiceNumber)
      return 'Lote sem número de nota fiscal.';

    if (batch.AutomaticEmail)
      return 'Este banco recebe o e-mail automaticamente.';

    return null;
  }

  get hasMoreLotsToLoad(): boolean {
    return this.count > this.lotsList.length;
  }

  // Quantidade de lotes selecionados para o envio. Com "selecionar todos" e mais paginas
  // por carregar, o total real so e conhecido pelo backend.
  get selectedCount(): number {
    if (!this.checkAll)
      return this.checkedBatches.length;

    return this.lotsList.filter((lote) => this.checkCardFlag(lote)).length;
  }

  get selectionLabel(): string {
    if (this.checkAll && this.hasMoreLotsToLoad)
      return 'todos selecionados';

    const total = this.selectedCount;

    return `${total} ${total === 1 ? 'selecionado' : 'selecionados'}`;
  }

  checkIncludedBatchId(batchId: string): boolean {
    return this.checkedBatches.indexOf(batchId) >= 0;
  }

  checkExceptionBatchId(batchId: string): boolean {
    return this.exceptionsBatches.indexOf(batchId) >= 0;
  }

  changeInvoiceCardCheckbox(batchId: string): void {
    if (!this.checkAll) {
      this.handleCheckedBatches(batchId);
    } else {
      this.handleExceptionsBacthes(batchId);
    }
  }

  handleCheckedBatches(batchId: string): void {
    let index = this.checkedBatches.indexOf(batchId);

    if (index >= 0) {
      this.checkedBatches.splice(index, 1);
    } else {
      this.checkedBatches.push(batchId);
    }
  }

  handleExceptionsBacthes(batchId: string): void {
    let index = this.exceptionsBatches.indexOf(batchId);

    if (index >= 0) {
      this.exceptionsBatches.splice(index, 1);
    } else {
      this.exceptionsBatches.push(batchId);
    }
  }

  handleSendInvoiceEmail(): void {
    if (this.checkAll || this.checkedBatches.length > 0) {
      const dialogData: IDialogConfirm = {
        type: DialogTypeEnum.Optional,
        appearance: 'modern',
        template: {
          boldTitle: 'Enviar e-mail',
          title: 'Caso prossiga, o disparo de e-mail será realizado de acordo com os lotes marcados. Deseja continuar?',
          confirm: 'Sim',
          dismiss: 'Não'
        }
      };

      this._dialogService.openDialog(dialogData).then((response) => {
        if (response) {
          this.sendInvoiceBatchEmail({
            BatchIds: [...this.checkedBatches],
            ExceptionsBatchIds: [...this.exceptionsBatches],
            SendAll: this.checkAll,
            Filter: {
              BatchStatus: true,
              BatchStatuses: this.filters.BatchStatuses ?? [],
              Text: this.search ?? '',
              Skip: 0,
              Take: 100,
              BatchDateFrom: this.batchDateFrom.value,
              BatchDateTo: this.batchDateTo.value,
              IsLegacy: this.filters.IsLegacy,
              IsZeroRate: this.filters.IsZeroRate,
              WithoutBatch: this.filters.WithoutBatch,
            }
          });
        }
      });
    }
  }

  sendInvoiceBatchEmail(batches: ISendBatchEmail): void {
    this._apiLotsService.sendInvoiceBatchEmail(batches).subscribe({
      next: (response) => {
        this.handleAfterSendEmail(response);
        this.getBatches();
      }
    });
  }

  handleAfterSendEmail(response: any): void {
    let allBatchsSent = true;

    if (Array.isArray(response) && response.length > 0)
      allBatchsSent = response.every((sent: any) => sent == null || (sent != null && sent.allBatchsSent == true || sent.allBatchsSent == null));
    else
      allBatchsSent = response.allBatchsSent;

    if (allBatchsSent == true || allBatchsSent == null)
      this._snackbarService.openSnackBar('Disparo de e-mail realizado com sucesso!', 'success');
    else
      this._snackbarService.openSnackBar('Alguns lotes não foram enviados!', 'warning');

    this.clearSelection();
    this.resetPagination();
  }

  // A selecao vale para a lista exibida: ao refiltrar/buscar, lotes que sairam da tela nao podem
  // continuar marcados (o envio de e-mail os incluiria sem o usuario ver).
  private clearSelection(): void {
    this.checkedBatches = [];
    this.exceptionsBatches = [];
    this.checkAll = false;
  }

  resetPagination(): void {
    this.skip = 0;
  }

  orderChange(fields: IFieldOrder[]): void {
    this.fieldsOrder = [...fields];
    this.getBatches(false, { Skip: 0, Take: this.skip + this.take });
  }

  getStorageFilter(): void {
    const bagSalva = localStorage.getItem('bag_i');
    const bag = JSON.parse(bagSalva || '{}');

    this.batchDateFrom.setValue(bag.dataIni);
    this.batchDateTo.setValue(bag.dataFim);
  }
}